import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HardDrive, Download, Clock, AlertTriangle, Activity } from 'lucide-react';
import { api, type CronJob, type Download as Dl, type NzbgetStatus, type Stats } from '../lib/api';
import { bytes, cronText, naechsterLauf, relativ } from '../lib/format';

/**
 * Uebersicht.
 *
 * Die alte Fassung zeigte "Total Downloads: 10" — das war die Laenge der
 * ersten Seite von `/downloads`, bei 812 Zeilen in der Datenbank. Jetzt
 * kommen die Zahlen aus `/downloads/stats` und meinen den ganzen Bestand.
 *
 * Neu und eigentlich das Wichtigste: **Plattenplatz**. Bei einem Werkzeug,
 * dessen Zweck es ist, eine 22-TB-Platte zu fuellen, gehoert der Fuellstand
 * auf die Startseite. Die Zahl liefert NZBGet ueber `/nzbget/status` — ein
 * Endpunkt, den es laengst gab und den niemand aufgerufen hat.
 */
export function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [letzte, setLetzte] = useState<Dl[]>([]);
  const [nzb, setNzb] = useState<NzbgetStatus | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    (async () => {
      // Jede Quelle einzeln absichern: Faellt NZBGet aus, soll trotzdem der
      // Rest der Seite stehen statt einer leeren Flaeche.
      const [s, j, d, n] = await Promise.allSettled([
        api<Stats>('/downloads/stats'),
        api<CronJob[]>('/cron-jobs'),
        api<Dl[]>('/downloads?limit=8'),
        api<NzbgetStatus>('/nzbget/status'),
      ]);
      if (weg) return;
      if (s.status === 'fulfilled') setStats(s.value);
      else setFehler(s.reason instanceof Error ? s.reason.message : String(s.reason));
      if (j.status === 'fulfilled') setJobs(j.value);
      if (d.status === 'fulfilled') setLetzte(d.value);
      if (n.status === 'fulfilled') setNzb(n.value);
    })();
    return () => { weg = true; };
  }, []);

  const aktiv = jobs.filter((j) => j.is_enabled);
  const naechster = aktiv
    .map((j) => naechsterLauf(j.cron_expression))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => a.getTime() - b.getTime())[0];

  const freiMB = typeof nzb?.FreeDiskSpaceMB === 'number' ? nzb.FreeDiskSpaceMB : null;

  return (
    <>
      <div className="kopf">
        <div>
          <h1>Übersicht</h1>
          <p>
            {aktiv.length
              ? <>{aktiv.length} {aktiv.length === 1 ? 'Auftrag sucht' : 'Aufträge suchen'} automatisch
                  {naechster && <> · nächster Lauf {relativ(naechster.toISOString())}</>}</>
              : 'Kein Auftrag aktiv — es wird nichts automatisch geladen.'}
          </p>
        </div>
      </div>

      {fehler && <div className="hinweis hinweis--schlecht" style={{ marginBottom: 16 }}>{fehler}</div>}

      <div className="kennzahlen">
        <div className="karte kennzahl">
          <div className="kennzahl__label"><Download size={13} /> Downloads gesamt</div>
          <div className="kennzahl__wert">{stats ? stats.total.toLocaleString('de-DE') : '—'}</div>
          <div className="kennzahl__zusatz">
            {stats ? <>{bytes(stats.total_bytes)} · {stats.completed.toLocaleString('de-DE')} fertig</> : 'lädt …'}
          </div>
        </div>

        <div className="karte kennzahl">
          <div className="kennzahl__label"><Clock size={13} /> Automatik</div>
          <div className="kennzahl__wert">{aktiv.length}<span className="schwach" style={{ fontSize: 17 }}> / {jobs.length}</span></div>
          <div className="kennzahl__zusatz">
            {stats?.by_source?.cron
              ? <>{stats.by_source.cron.toLocaleString('de-DE')} Downloads kamen von hier</>
              : 'aktiv / angelegt'}
          </div>
        </div>

        <div className="karte kennzahl">
          <div className="kennzahl__label"><AlertTriangle size={13} /> Fehlgeschlagen</div>
          <div className="kennzahl__wert" style={{ color: stats && stats.failed > 0 ? 'var(--schlecht)' : undefined }}>
            {stats ? stats.failed.toLocaleString('de-DE') : '—'}
          </div>
          <div className="kennzahl__zusatz">
            {stats && stats.total > 0
              ? `${Math.round((stats.failed / stats.total) * 100)} % aller Versuche`
              : '—'}
          </div>
        </div>

        <Platte freiMB={freiMB} />
      </div>

      {stats && stats.in_flight > 0 && (
        <div className="hinweis" style={{ marginBottom: 16 }}>
          <Activity size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          {stats.in_flight} {stats.in_flight === 1 ? 'Download läuft' : 'Downloads laufen'} gerade.
        </div>
      )}

      <div className="karte">
        <div className="karte__kopf">
          <span className="karte__titel">Zuletzt geladen</span>
          <Link to="/downloads" className="klein">alle ansehen →</Link>
        </div>
        {letzte.length === 0 ? (
          <div className="karte__leer">Noch nichts geladen.</div>
        ) : (
          <table className="tabelle">
            <thead>
              <tr>
                <th>Titel</th>
                <th style={{ width: 110 }}>Quelle</th>
                <th style={{ width: 110 }}>Status</th>
                <th className="num" style={{ width: 90 }}>Größe</th>
                <th className="num" style={{ width: 100 }}>Wann</th>
              </tr>
            </thead>
            <tbody>
              {letzte.map((d) => (
                <tr key={d.id}>
                  <td className="titel-zelle">{d.title}</td>
                  <td><span className="chip chip--neutral">{d.source ?? '—'}</span></td>
                  <td><StatusChip status={d.status} /></td>
                  <td className="num klein leise">{bytes(d.size_bytes)}</td>
                  <td className="num klein leise">{relativ(d.downloaded_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {aktiv.length > 0 && (
        <div className="karte" style={{ marginTop: 16 }}>
          <div className="karte__kopf">
            <span className="karte__titel">Aktive Aufträge</span>
            <Link to="/cron-jobs" className="klein">verwalten →</Link>
          </div>
          <table className="tabelle">
            <tbody>
              {aktiv.map((j) => {
                const n = naechsterLauf(j.cron_expression);
                return (
                  <tr key={j.id}>
                    <td style={{ fontWeight: 600 }}>{j.name}</td>
                    <td className="klein leise">{cronText(j.cron_expression)}</td>
                    <td className="num klein leise">{n ? `nächster ${relativ(n.toISOString())}` : ''}</td>
                    <td className="num klein leise">zuletzt {relativ(j.last_run_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Platte({ freiMB }: { freiMB: number | null }) {
  if (freiMB === null) {
    return (
      <div className="karte kennzahl">
        <div className="kennzahl__label"><HardDrive size={13} /> Freier Platz</div>
        <div className="kennzahl__wert schwach">—</div>
        <div className="kennzahl__zusatz">NZBGet nicht erreichbar</div>
      </div>
    );
  }
  const freiGB = freiMB / 1024;
  // Nur der freie Platz ist bekannt, nicht die Gesamtgroesse — deshalb wird
  // hier kein Prozentwert erfunden, sondern nach Restmenge eingefaerbt.
  const stufe = freiGB < 200 ? 'schlecht' : freiGB < 800 ? 'warn' : 'gut';
  const anteil = Math.max(4, Math.min(100, (freiGB / 2000) * 100));
  return (
    <div className="karte kennzahl">
      <div className="kennzahl__label"><HardDrive size={13} /> Freier Platz</div>
      <div className="kennzahl__wert" style={{
        color: stufe === 'schlecht' ? 'var(--schlecht)' : stufe === 'warn' ? 'var(--warn)' : undefined,
      }}>
        {freiGB >= 1024 ? `${(freiGB / 1024).toFixed(1)} TB` : `${Math.round(freiGB)} GB`}
      </div>
      <div className="kennzahl__zusatz">
        {stufe === 'schlecht' ? 'wird eng' : stufe === 'warn' ? 'im Blick behalten' : 'reichlich'}
      </div>
      <div className={`balken${stufe === 'gut' ? '' : ` balken--${stufe}`}`}>
        <i style={{ width: `${anteil}%` }} />
      </div>
    </div>
  );
}

export function StatusChip({ status }: { status: string }) {
  const art =
    status === 'completed' ? 'gut'
    : status === 'failed' ? 'schlecht'
    : status === 'downloading' || status === 'pushed' ? 'akzent'
    : 'neutral';
  const text =
    status === 'completed' ? 'fertig'
    : status === 'failed' ? 'fehlgeschlagen'
    : status === 'downloading' ? 'lädt'
    : status === 'pushed' ? 'übergeben'
    : status;
  return <span className={`chip chip--${art}`}>{text}</span>;
}
